import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatBC } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, Shield } from "lucide-react";

export default function AdminOperators() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async (query = "") => {
    const { data } = await api.get("/admin/users" + (query ? `?q=${encodeURIComponent(query)}` : ""));
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const toggleRole = async (u) => {
    if (u.id === me.id) { toast.error("You cannot change your own role."); return; }
    const next = u.role === "admin" ? "user" : "admin";
    if (!window.confirm(`Set ${u.email} as ${next.toUpperCase()}?`)) return;
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: next });
      toast.success(`${u.email} is now ${next.toUpperCase()}.`);
      await load(q);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-operators-panel">
      <div className="flex flex-wrap items-center gap-3">
        <input
          className="zfd-input max-w-xs"
          placeholder="Search by email or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="op-search"
        />
        <button onClick={() => load(q)} className="btn-deploy text-xs" data-testid="op-search-btn">Search</button>
        <span className="text-xs text-white/45 ml-auto">{users.length} operator{users.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="glass-card p-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                <th className="text-left p-2">Name</th>
                <th className="text-left">Email</th>
                <th className="text-left">Role</th>
                <th className="text-center">Fraud Flags</th>
                <th className="text-right">Balance</th>
                <th className="text-right">Total Deposited</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={7} className="text-center text-white/40 py-6">No operators match.</td></tr>
              )}
              {users.map((u) => {
                const isMe = u.id === me.id;
                const isAdmin = u.role === "admin";
                const flags = u.fraud_flags || 0;
                return (
                  <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`op-row-${u.id}`}>
                    <td className="p-2 text-xs">{u.full_name}{isMe && <span className="ml-1 text-[10px] text-[#FBBF24]">(you)</span>}</td>
                    <td className="text-xs text-white/70">{u.email}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${isAdmin ? "bg-[#DC2626]/15 text-[#FCA5A5] border-[#DC2626]/40" : "bg-white/5 text-white/70 border-white/10"}`}>
                        {isAdmin ? <ShieldCheck size={12} /> : <Shield size={12} />} {u.role}
                      </span>
                    </td>
                    <td className="text-center">
                      <span
                        title={u.last_fraud_at ? `Last flag: ${new Date(u.last_fraud_at).toLocaleString()}` : "No fraud flags"}
                        className={`inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-full text-[11px] font-mono-num font-bold ${flags === 0 ? "bg-white/5 text-white/35" : flags < 3 ? "bg-yellow-500/15 text-yellow-300" : "bg-[#DC2626]/20 text-[#FCA5A5]"}`}
                        data-testid={`fraud-flags-${u.id}`}
                      >
                        {flags}
                      </span>
                    </td>
                    <td className="text-right font-mono-num text-[#FBBF24]">₱{formatBC(u.active_balance)}</td>
                    <td className="text-right font-mono-num text-white/70">₱{formatBC(u.total_deposited)}</td>
                    <td className="text-right">
                      <button
                        disabled={isMe || busy === u.id}
                        onClick={() => toggleRole(u)}
                        className={`px-2 py-1 rounded text-xs font-bold ${isAdmin ? "bg-white/10 hover:bg-white/15" : "bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300"} disabled:opacity-30`}
                        data-testid={`role-toggle-${u.id}`}
                      >
                        {isAdmin ? "Demote to user" : "Promote to admin"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
